import { getAddons } from '../../global/pricingConfig';
import {
  MonetaryValue,
  PricingPlanAddon,
  PricingPlanPrice,
} from '../../types/Pricing';

/**
 * Calculates the monthly and yearly costs per month for a given pricing plan.
 *
 * @param {Object} params - The parameters for calculating the plan costs.
 * @param {PricingPlanPrice} params.planPricing - The pricing details of the plan, including monthly and yearly prices.
 * @param {boolean} params.yearly - A flag indicating whether the yearly plan is selected.
 * @param {number} params.quantity - The quantity of the plan being purchased.
 * @param {PricingPlanAddon} [params.selectedAddon] - An optional addon that may increase the total amount.
 * @returns {Object} - An object containing the calculated monthly and yearly costs per month.
 *
 * The function calculates the monthly and yearly costs per month based on the provided pricing plan,
 * quantity, and any selected addon. It returns an object with the calculated costs.
 */
export const getPlanCostsPerMonth = ({
  planPricing,
  selectedAddon,
  yearly,
  quantity = 1,
}: {
  planPricing: PricingPlanPrice;
  selectedAddon?: PricingPlanAddon;
  yearly: boolean;
  quantity?: number;
}): Record<'yearly' | 'monthly', MonetaryValue> => {
  // Get the base monthly and yearly amounts
  const yearlyAmount = planPricing.yearly.amount;
  const monthlyAmount = planPricing.monthly.amount;

  // Calculate the addon amount if provided
  const addonIndex =
    planPricing[yearly ? 'yearly' : 'monthly'].addons?.findIndex(
      (addon) => addon.stripeId === selectedAddon?.stripeId
    ) ?? -1;
  const monthlyAddons = getAddons();
  const yearlyAddons = getAddons();

  return {
    monthly: {
      amount:
        monthlyAmount * quantity + (monthlyAddons[addonIndex]?.amount ?? 0),
      currency: planPricing.monthly.currency,
    },
    yearly: {
      amount: yearly
        ? yearlyAmount * quantity + (yearlyAddons[addonIndex]?.amount ?? 0)
        : 0,
      currency: planPricing.yearly.currency,
    },
  };
};
